import { useEffect, useRef, useState } from 'react';
import { getPublicForm } from './api';
import { applyTheme } from './theme';
import { FormRenderer } from './components/FormRenderer';
import { LoadingScreen } from './components/LoadingScreen';
import { ErrorScreen } from './components/ErrorScreen';
import type { PublicForm } from './types';
import './styles/base.css';

export function App() {
  const contentRef = useRef<HTMLDivElement>(null);
  // Detect the frame itself: payment return URLs need not retain query parameters.
  const embedded = window.parent !== window &&
    new URLSearchParams(window.location.search).get('preview') !== '1';

  useEffect(() => {
    const content = contentRef.current;
    if (!embedded || !content) return;
    const reportHeight = () => {
      // Measure content, not the document viewport, so shorter states can shrink.
      const height = Math.ceil(Math.max(content.getBoundingClientRect().height, content.scrollHeight));
      // Only dimensions are shared; the host validates this frame's source/origin.
      window.parent.postMessage({ type: 'engage:resize', height }, '*');
    };
    const observer = new ResizeObserver(reportHeight);
    observer.observe(content);
    reportHeight();
    return () => observer.disconnect();
  }, [embedded]);

  // A formatting context includes child margins in the measured content height.
  return <div ref={contentRef} style={{ display: 'flow-root' }}><AppContent /></div>;
}

function AppContent() {
  // Live, via the worker: "/{tenantSlug}/{formId}" (ADR-009) — the worker
  // validates and forwards the tenant slug, but never rewrites the browser's own
  // URL, so window.location keeps both segments. A single segment only happens
  // testing the renderer directly, bypassing the worker (e.g. the builder
  // preview's local override, which posts just the formId — see
  // FormPreviewIframe.tsx); that path never fetches, so no tenant is needed.
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const tenantSlug = pathParts.length >= 2 ? pathParts[0] : undefined;
  const formId = pathParts.length >= 2 ? pathParts[1] : (pathParts[0] ?? '');
  const params = new URLSearchParams(window.location.search);
  const previewMode = params.get('preview') === '1';
  const previewEmbedKey = params.get('embedKey') ?? '';
  // create_direct_debit_flow sends the supporter back here after GoCardless,
  // with `?dd=complete` or `?dd=cancelled` (plus the submission id, which only
  // the server needs). Anything else in `dd` is ignored rather than trusted.
  const ddParam = params.get('dd');
  const directDebitReturn =
    ddParam === 'complete' || ddParam === 'cancelled' ? ddParam : undefined;

  const [form, setForm] = useState<PublicForm | null>(null);
  // In preview the key comes from the query string; outside preview it arrives
  // with the fetched form below.
  const [embedKey, setEmbedKey] = useState(previewMode ? previewEmbedKey : '');
  const [error, setError] = useState(
    previewMode ? '' : !tenantSlug ? 'No tenant in URL' : !formId ? 'No form ID in URL' : '',
  );

  // The builder preview renders solely from the postMessage payload handled below.
  // It deliberately does not call the public API: that endpoint serves only
  // *published* forms, and outside preview the tenant travels in the URL path
  // rather than the Host header (ADR-009) — an in-editor draft has neither, and a
  // rejected fetch would set an error that outlives the form the builder
  // subsequently sends.
  useEffect(() => {
    if (previewMode || !tenantSlug || !formId) return;
    getPublicForm(tenantSlug, formId)
      .then(f => {
        setForm(f);
        setEmbedKey(previewEmbedKey || '');
        applyTheme(f.theme);
      })
      .catch(e => setError(e.message));
  }, [tenantSlug, formId, previewMode, previewEmbedKey]);

  // Listen for postMessage theme/field updates from the builder preview.
  // Only accept messages from the configured builder origin to prevent malicious embeds
  // from overwriting form content or injecting CSS via customCss.
  // Fails closed: rejects all messages when VITE_BUILDER_ORIGIN is not set.
  useEffect(() => {
    const builderOrigin = import.meta.env.VITE_BUILDER_ORIGIN ?? '';
    const handler = (e: MessageEvent) => {
      if (!builderOrigin || e.origin !== builderOrigin) return;
      if (e.data?.type === 'engage:preview-update' && e.data.form) {
        setForm(e.data.form as PublicForm);
        applyTheme((e.data.form as PublicForm).theme);
        setError('');
      }
    };
    window.addEventListener('message', handler);
    // Announce readiness only after the listener is attached. The builder also
    // posts on the iframe's load event, but whether that fires before this effect
    // runs is not guaranteed — and with no API fallback a dropped first message
    // would leave the preview blank until the next edit.
    if (previewMode && builderOrigin && window.parent !== window) {
      window.parent.postMessage({ type: 'engage:preview-ready' }, builderOrigin);
    }
    return () => window.removeEventListener('message', handler);
  }, [previewMode]);

  // A delivered form always wins over a stale error (belt to the setError('') braces above).
  if (form) {
    return (
      <FormRenderer
        form={form}
        embedKey={embedKey}
        tenantSlug={tenantSlug ?? ''}
        previewMode={previewMode}
        directDebitReturn={directDebitReturn}
      />
    );
  }
  if (error) return <ErrorScreen message={error} />;
  return <LoadingScreen />;
}
